import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import { isArray } from 'lodash';
import { Form } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import Button from '../ui/Button';
import Loader from '../ui/Loader';
import appConfig from '../../appConfig';
import { allowDropHandler } from '../../utils/uiUtils';
import { SearchParams } from '../../utils/searchUtils';
import { parseTag } from '../../utils/metadataUtils';
import { metaAttach, getUmoMetadata } from '../../actions/metaAttach';
import { stringifiedToDraggable } from '../../utils/draggables';
import {
  META_ATTACH_STATE_IN_PROGRESS,
  META_ATTACH_STATE_FETCHING_DATA,
  META_ATTACH_STATE_FAILURE,
  META_ATTACH_STATE_READY,
} from '../../constants/metaAttachStates';
import { deselectAll } from '../../actions/umoGallery';
import { retrieveUMO } from '../../actions/umo';
import PictionJS from 'piction-js';
import UMOThumbnail from '../UMOThumbnail';

function MetaPopulate({
  intl,
  dispatch,
  metaAttachState,
  umo,
  umoMetadata,
  selectedUMOIDs,
  searchParams,
  isSelectAll,
}) {
  const [isReplace, setReplace] = useState(false);
  const [tagIndex, setTagIndex] = useState(0);
  const [desc, setDesc] = useState('');
  const isLoading = metaAttachState === META_ATTACH_STATE_IN_PROGRESS
  const isFetchingData = metaAttachState === META_ATTACH_STATE_FETCHING_DATA;
  const umoInsertMetatags = [];
  let mediaPopulateDescMetatag;
  let metaSelect;

  useEffect(() => {
    if (umoMetadata && umoMetadata.length === 0 && !isFetchingData) {
      dispatch(getUmoMetadata(umo.id))
    }
  }, [umo.id]);

  useEffect(() => {
    if (appConfig.advanced.mediaPopulateDescMetatag) {
      dispatch(retrieveUMO(umo.id))
      .then(res => {
        setDesc(res.metadataList?.getMetadata(appConfig.advanced.mediaPopulateDescMetatag)?.value || '');
      })
    }
  }, [umo.id])

  const attachMetadata = (umoIDList, params) => {
    const tags = [umoInsertMetatags[tagIndex]];
    tags[0].value = umoIDList;

    if (mediaPopulateDescMetatag) {
      mediaPopulateDescMetatag.value = desc;
      mediaPopulateDescMetatag.isReplace = true;
      tags.push(mediaPopulateDescMetatag)
    }

    dispatch(metaAttach(
      tags,
      umo.id,
      isReplace,
      params,
      intl
    ))
      .then(() => dispatch(deselectAll()));
  };

  const onAttachSelectedClick = () => {
    attachMetadata(selectedUMOIDs.toJS(),
      isSelectAll ? searchParams : null);
  };

  const dropHandler = (e) => {
    e.preventDefault();
    const draggable = stringifiedToDraggable(e.dataTransfer.getData('text'));
    if (!(draggable instanceof Error)) {
      attachMetadata(
        isArray(draggable.getSelectionList()) ?
          draggable.getSelectionList() : [],
        draggable.getIsSelectAll() ? draggable.getSearchParams() : null
      );
    }
  };

  const errFeedback = (<div className="invalid-feedback" style={{ display: 'block' }}>
    {intl.formatMessage({ id: 'metaAttach.populateMetatagDoesntExist' })}
  </div>);

  if (umoMetadata && umoMetadata.length > 0) {
    umoMetadata.forEach(item => {
      if (item.CORE_TYPE[0] === 'UMO_INSERT') {
        umoInsertMetatags.push(parseTag(item.$.TAG_ID, item, item.TEXT || []));
      } else if (item.$.TAG_ID === appConfig.advanced?.mediaPopulateDescMetatag) {
        mediaPopulateDescMetatag = parseTag(item.$.TAG_ID, item, item.TEXT)
      }
    });
    if (umoInsertMetatags.length > 0) {
      metaSelect = (<select className="form-select form-select-sm"
                            onChange={e => setTagIndex(e.target.value)}>
        {umoInsertMetatags.map((item, i) => {
          return (
            <option value={i} key={item.id}>
              {item.label}
            </option>);
        })}
      </select>);
    } else metaSelect = errFeedback;
  } else {
    metaSelect = isFetchingData ? <Loader isLarge={false} /> : errFeedback;
  }

  let isDisabled = isLoading || umoInsertMetatags.length === 0 || (isSelectAll && !searchParams) ||
    (!isSelectAll && selectedUMOIDs.count() < 1);

  return (
    <div className="meta-attach__media-populate meta-attach__drop-area">
      <div
        className="meta-attach__controls-container"
        onDrop={dropHandler}
        onDragOver={isLoading || umoInsertMetatags.length === 0 ? undefined : allowDropHandler}
      >
        <UMOThumbnail umo={umo} noSensitiveOverlay />
        { // don't display the drop area on IE browsers
          navigator.msSaveBlob ? null :
            <div className="meta-attach__drop-inner">
              <i className="meta-attach__drop-inner-icon" />
              <p className="meta-attach__drop-inner-text">
                {intl.formatMessage({id: 'metaAttach.metaPopulateDragDrop'},
                  {tagName: (umoInsertMetatags.length > 0 && umoInsertMetatags[tagIndex] && umoInsertMetatags[tagIndex].label) || ''})}
              </p>
            </div>
        }
        <Button
          primary
          disabled={isDisabled}
          onClick={onAttachSelectedClick}
          className="meta-attach__submit-btn"
        >
          { isLoading ? <Loader /> : intl.formatMessage({ id: 'metaAttach.PopulateButton' }) }
        </Button>
        { metaSelect }
        <Form.Group className="meta-attach__controls-container__replace-check">
          <Form.Check
            type="checkbox"
            id="meta-attach__replace-check"
            onChange={() => setReplace(!isReplace)}
            checked={isReplace}
            label={intl.formatMessage({ id: 'metaAttach.replaceCheckbox' })}
          />
          <Form.Text className="text-muted meta-attach__help-text">
            {intl.formatMessage({ id: 'metaAttach.defaultAttachBehavior' })}
          </ Form.Text>
        </Form.Group>
        { mediaPopulateDescMetatag ?
        <div>
          <br />
          {intl.formatMessage({ id: 'metaAttach.RelationshipDesc' })}
          <textarea className='form-control meta-attach__desc' rows={3} value={desc} onChange={e => setDesc(e.target.value)} />
        </div>
        : null}
        { metaAttachState === META_ATTACH_STATE_FAILURE ?
          <div className="invalid-feedback" style={{ display: 'block' }}>
            {intl.formatMessage({ id: 'general.error' })}
          </div> : null
        }
      </div>
    </div>
  );
}

MetaPopulate.propTypes = {
  metaAttachState: PropTypes.string,
  dispatch: PropTypes.func,
  umo: PropTypes.instanceOf(PictionJS.UMO),
  umoMetadata: PropTypes.array,
  selectedUMOIDs: PropTypes.instanceOf(List),
  searchParams: PropTypes.instanceOf(SearchParams),
  isSelectAll: PropTypes.bool,
};

export default injectIntl(MetaPopulate);
